import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

function markOfflineWrite(){
  if(typeof window==='undefined'||navigator.onLine)return;
  try{window.dispatchEvent(new CustomEvent('coachsphere:pending-write',{detail:{delta:1}}))}catch{}
}

export function subscribeTenantCollection(collectionName, tenantId, callback, onError) {
  if (!tenantId) return () => {};
  const q = query(collection(db, collectionName), where('tenantId', '==', tenantId));
  return onSnapshot(q, (snapshot) => {
    const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    rows.sort((a, b) => {
      const av = a.createdAt?.toMillis?.() || 0;
      const bv = b.createdAt?.toMillis?.() || 0;
      return bv - av;
    });
    callback(rows);
  }, onError);
}

export async function fetchTenantCollection(collectionName, tenantId) {
  const q = query(collection(db, collectionName), where('tenantId', '==', tenantId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createTenantRecord(collectionName, tenantId, actorId, data) {
  markOfflineWrite();
  return addDoc(collection(db, collectionName), {
    ...data,
    tenantId,
    createdAt: serverTimestamp(),
    createdBy: actorId,
    updatedAt: serverTimestamp(),
    updatedBy: actorId,
  });
}

export async function updateTenantRecord(collectionName, id, actorId, data, tenantId = '') {
  markOfflineWrite();
  const target = doc(db, collectionName, id);
  if (tenantId) {
    const current = await getDoc(target);
    if (!current.exists()) throw new Error('Record no longer exists. Refresh this page and try again.');
    if (current.data()?.tenantId !== tenantId) throw new Error('Tenant mismatch. This record cannot be edited from the current institute.');
  }
  const { id: _id, tenantId: _tenantId, createdAt: _createdAt, createdBy: _createdBy, ...safeData } = data || {};
  return updateDoc(target, {
    ...safeData,
    updatedAt: serverTimestamp(),
    updatedBy: actorId,
  });
}

export async function deleteTenantRecord(collectionName, id, tenantId = '') {
  markOfflineWrite();
  const target = doc(db, collectionName, id);
  if (tenantId) {
    const current = await getDoc(target);
    if (!current.exists()) return;
    if (current.data()?.tenantId !== tenantId) throw new Error('Tenant mismatch. This record cannot be deleted from the current institute.');
  }
  return deleteDoc(target);
}


export async function bulkCreateTenantRecords(collectionName, tenantId, actorId, records = []) {
  if (!tenantId) throw new Error('Tenant is required for bulk import.');
  const safe = Array.isArray(records) ? records.filter(Boolean) : [];
  if (!safe.length) return { created: 0, ids: [] };
  markOfflineWrite();
  const ids=[];
  for (let offset=0; offset<safe.length; offset+=400) {
    const batch=writeBatch(db);
    safe.slice(offset,offset+400).forEach((data)=>{
      const ref=doc(collection(db,collectionName));
      const {id:_id,tenantId:_tenantId,createdAt:_createdAt,createdBy:_createdBy,updatedAt:_updatedAt,updatedBy:_updatedBy,...payload}=data||{};
      batch.set(ref,{...payload,tenantId,createdAt:serverTimestamp(),createdBy:actorId,updatedAt:serverTimestamp(),updatedBy:actorId,imported:true});
      ids.push(ref.id);
    });
    await batch.commit();
  }
  return { created: ids.length, ids };
}

export async function bulkDeleteTenantRecords(collectionName, ids = [], tenantId = '') {
  const unique=[...new Set((ids||[]).filter(Boolean))];
  if (!unique.length) return { deleted: 0 };
  if (!tenantId) throw new Error('Tenant is required for bulk delete.');
  const snapshots=await Promise.all(unique.map(id=>getDoc(doc(db,collectionName,id))));
  const invalid=snapshots.find(s=>s.exists()&&s.data()?.tenantId!==tenantId);
  if (invalid) throw new Error('Tenant mismatch detected. Bulk delete was cancelled before any record was removed.');
  markOfflineWrite();
  let deleted=0;
  for (let offset=0; offset<unique.length; offset+=400) {
    const batch=writeBatch(db);
    const chunk=unique.slice(offset,offset+400);
    chunk.forEach(id=>batch.delete(doc(db,collectionName,id)));
    await batch.commit();
    deleted+=chunk.length;
  }
  return { deleted };
}
